import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';

import { CreateCoachMeetingCmd } from 'task/command/create-coach-meeting.cmd';
import { CreateContactMeetingCmd } from 'task/command/create-contact-meeting.cmd';
import { CreateJobOfferMeetingCmd } from 'task/command/create-job-offer-meeting.cmd';
import { CreateTodoTaskCmd } from 'task/command/create-todo-task.cmd';
import { MoveTaskCmd } from 'task/command/move-task.cmd';
import { TaskOutput } from 'task/model/task.output';
import { CreateCoachMeetingApiMutation } from 'task/mutation/create-coach-meeting.mutation';
import { CreateContactMeetingApiMutation } from 'task/mutation/create-contact-meeting.mutation';
import { CreateJobOfferMeetingApiMutation } from 'task/mutation/create-job-offer-meeting.mutation';
import { CreateTodoTaskApiMutation } from 'task/mutation/create-todo-task.mutation';
import { DeleteTaskApiMutation } from 'task/mutation/delete-task.mutation';
import { MarkTaskDoneApiMutation } from 'task/mutation/mark-task-done.mutation';
import { MarkTaskPendingApiMutation } from 'task/mutation/mark-task-pending.mutation';
import { UpdateTaskApiMutation } from 'task/mutation/update-task.mutation';
import { TaskApiPort } from 'task/port/task-api.port';
import { FetchContactTasksApiQuery } from 'task/query/fetch-contact-tasks.query';
import { FetchDayTasksApiQuery } from 'task/query/fetch-day-tasks.api-query';
import { FetchJobOfferTasksApiQuery } from 'task/query/fetch-job-offer-tasks.query';
import { FetchPendingTasksByUserIdApiQuery } from 'task/query/fetch-pending-tasks-by-user-id.query';
import { FetchTaskByIdApiQuery } from 'task/query/fetch-task-by-id.query';
import { FetchTasksByIdsApiQuery } from 'task/query/fetch-tasks-by-ids.query';
import { FetchTasksApiQuery } from 'task/query/fetch-tasks.api-query';
import { FetchTasksQuery } from 'task/query/fetch-tasks.query';
import { ObserveSearchedUserCoachMeetingsQuery } from 'task/query/observe-searched-user-coach-meetings.query';
import { SearchUserCoachMeetingsApiQuery } from 'task/query/search-user-coach-meetings.query';
import { UserSessionPort } from 'user/port/user-session.port';
import { FetchUsersByIdsApiQuery } from 'user/query/fetch-users-by-ids.query';

import { ApiAdapter } from '../../common/api.adpater';

@Injectable()
export class TaskApiAdapter extends ApiAdapter implements TaskApiPort {
	constructor(
		userSessionPort: UserSessionPort,
		translateService: TranslateService,
		private _fetchDayTasksApiQuery: FetchDayTasksApiQuery,
		private _fetchTaskByIdApiQuery: FetchTaskByIdApiQuery,
		private _deleteTaskApiMutation: DeleteTaskApiMutation,
		private _updateTaskApiMutation: UpdateTaskApiMutation,
		private _fetchUsersByIdsApiQuery: FetchUsersByIdsApiQuery,
		private _markTaskDoneApiMutation: MarkTaskDoneApiMutation,
		private _fetchContactTasksApiQuery: FetchContactTasksApiQuery,
		private _fetchJobOfferTasksApiQuery: FetchJobOfferTasksApiQuery,
		private _markTaskPendingApiMutation: MarkTaskPendingApiMutation,
		private _fetchTasksApiQuery: FetchTasksApiQuery,
		private _createCoachMeetingApiMutation: CreateCoachMeetingApiMutation,
		private _createTodoTaskApiMutation: CreateTodoTaskApiMutation,
		private _createContactMeetingApiMutation: CreateContactMeetingApiMutation,
		private _createJobOfferMeetingApiMutation: CreateJobOfferMeetingApiMutation,
		private _fetchPendingTasksByUserIdApiQuery: FetchPendingTasksByUserIdApiQuery,
		private _fetchTasksByIdsApiQuery: FetchTasksByIdsApiQuery,
		private _searchUserCoachMeetingsApiQuery: SearchUserCoachMeetingsApiQuery
	) {
		super(userSessionPort, translateService);
	}

	public fetchDayTasks(date: string): Observable<TaskOutput[]> {
		return this.fetch$(this._fetchDayTasksApiQuery, { date });
	}

	public moveTask(cmd: MoveTaskCmd): Observable<TaskOutput> {
		return from(this.mutate(this._updateTaskApiMutation, { input: cmd }));
	}

	public createJobOfferMeeting(
		cmd: CreateJobOfferMeetingCmd
	): Observable<TaskOutput> {
		return from(
			this.mutate(this._createJobOfferMeetingApiMutation, { input: cmd })
		);
	}

	public createContactMeeting(
		cmd: CreateContactMeetingCmd
	): Observable<TaskOutput> {
		return from(
			this.mutate(this._createContactMeetingApiMutation, { input: cmd })
		);
	}

	public createCoachMeeting(cmd: CreateCoachMeetingCmd): Observable<TaskOutput> {
		return from(this.mutate(this._createCoachMeetingApiMutation, { input: cmd }));
	}

	public createTodoTask(cmd: CreateTodoTaskCmd): Observable<TaskOutput> {
		return from(this.mutate(this._createTodoTaskApiMutation, { input: cmd }));
	}

	public deleteTask(id: string): Promise<string> {
		return from(this.mutate(this._deleteTaskApiMutation, { id })).toPromise();
	}

	public fetchTasks(query: FetchTasksQuery): Observable<TaskOutput[]> {
		return this.fetch$(this._fetchTasksApiQuery, query);
	}

	public fetchJobOfferTasks(jobOfferId: string): Observable<TaskOutput[]> {
		return this.fetch$(this._fetchJobOfferTasksApiQuery, { jobOfferId });
	}

	public fetchContactTasks(contactId: string): Observable<TaskOutput[]> {
		return this.fetch$(this._fetchContactTasksApiQuery, { contactId });
	}

	public fetchTaskById(id: string): Promise<TaskOutput> {
		return from(this.fetch(this._fetchTaskByIdApiQuery, { id })).toPromise();
	}

	public fetchUserPendingTasks(userId: string): Observable<TaskOutput[]> {
		return from(this.fetch(this._fetchPendingTasksByUserIdApiQuery, { userId }));
	}

	public markTaskDone(id: string): Promise<TaskOutput> {
		return from(this.mutate(this._markTaskDoneApiMutation, { id })).toPromise();
	}

	public markTaskPending(id: string): Promise<TaskOutput> {
		return from(
			this.mutate(this._markTaskPendingApiMutation, { id })
		).toPromise();
	}

	public fetchTasksByIds(ids: string[]): Promise<TaskOutput[]> {
		return from(this.fetch(this._fetchTasksByIdsApiQuery, { ids })).toPromise();
	}

	public searchUserCoachMeetings(
		query: ObserveSearchedUserCoachMeetingsQuery
	): Observable<TaskOutput[]> {
		return this.fetch$(this._searchUserCoachMeetingsApiQuery, {
			userId: query.userId,
			offset: query.offset,
			sortProperty: query.sortProperty,
			sortDirection: query.sortDirection,
			dueStartTimestamp: query.dueStartTimestamp,
			description: query.description,
			dueEndTimestamp: query.dueEndTimestamp,
			statuses: query.statuses,
			chunkSize: query.chunkSize,
		}).pipe(map((result) => result.elements));
	}
}
